import React from 'react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { config } from 'appConfig';

interface PosthogEventProperties {
  page?: string;
  feature?: string;
  action?: string;
  query?: string;
  input?: Record<string, unknown>;
  extra?: Record<string, unknown>;
}

if (config.posthogApiKey && config.posthogShouldTrack) {
  posthog.init(config.posthogApiKey, {
    secure_cookie: true,
    api_host: 'https://sentinela.leadzai.com', // Posthog reverse proxy
    // ui_host: used for FeatureFlags and Toolbar (value ex.: https://us.posthog.com)
    autocapture: config.posthogShouldTrack,
    capture_pageview: false, // we capture manually, check AppRouting
    capture_exceptions: true,
    capture_performance: true,
    capture_heatmaps: true,
  });
}

export const userIdentifyEvent = (userPrettyID: string, userData = {}) => {
  if (config.posthogShouldTrack) {
    posthog.identify(userPrettyID, { ...userData });
  }
};

export const userRegisterEvent = (properties: Record<string, unknown> = {}) => {
  if (config.posthogShouldTrack) {
    posthog.register(properties);
  }
};

export const setPersonProperties = (properties: Record<string, unknown> = {}) => {
  if (config.posthogShouldTrack) {
    posthog.setPersonProperties(properties);
  }
};

export const resetTracking = (logMessage?: string) => {
  if (config.posthogShouldTrack) {
    if (logMessage) {
      posthog.capture(logMessage);
    }
    posthog.reset();
  }
};

export const stopTracking = () => {
  if (config.posthogShouldTrack) {
    posthog.opt_out_capturing();
  }
};

export const logPosthogEvent = (name: string, properties: PosthogEventProperties) => {
  if (config.posthogShouldTrack) {
    posthog.capture(name, properties);
  }
};

export const logPosthogException = (error: Error, properties: Record<string, unknown> = {}) => {
  if (config.posthogShouldTrack) {
    posthog.captureException(error, {
      stack: error.stack,
      ...properties,
    });
  }
};

export const AppPosthogProvider = (children: React.ReactNode) => {
  if (!config.posthogShouldTrack || !config.posthogApiKey) {
    return <>{children}</>;
  }

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
};
